import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Seo from "@/components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token. Request a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { toast.error("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Seo title="Choose a new password" description="Choose a new password for your Strategic Tracking account."/>
      {done ? (
        <div className="text-center" data-testid="reset-done">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500"/>
          <h1 className="font-display font-extrabold text-2xl text-slate-900 mt-4">Password updated</h1>
          <p className="mt-3 text-sm text-slate-600">Sending you to sign in…</p>
          <Link to="/login" className="btn-primary mt-6 inline-flex" data-testid="reset-go-login">Go to sign in</Link>
        </div>
      ) : (
        <>
          <div className="w-12 h-12 rounded-md bg-slate-900 text-white grid place-items-center"><Lock size={18}/></div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-3">Choose a new password</h1>
          <p className="text-sm text-slate-500 mt-2">Minimum 8 characters. This link is single-use.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">New password</Label>
              <Input type="password" autoFocus minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-pw"/>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Confirm new password</Label>
              <Input type="password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm"/>
            </div>
            <button type="submit" disabled={submitting || !token} className="btn-primary w-full justify-center h-11" data-testid="reset-submit">
              {submitting ? "Updating…" : "Update password"} <ArrowRight size={14}/>
            </button>
            {!token && (
              <p className="text-sm text-red-600 text-center">
                No reset token — <Link to="/forgot-password" className="underline">request a new link</Link>.
              </p>
            )}
          </form>
        </>
      )}
    </div>
  );
}
